import { CalculatorIcon, PencilIcon, PlusIcon } from '@heroicons/react/outline';
import { Button, IconButton, Stack } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';

import { IContactHeaderCreditScore, ICreditScore, TabIndexEnum } from '@modules/ContactManager/components/Header/interfaces';
import { ContactManagerLayout } from '@modules/ContactManager/components/Tabs/shared/ContactManagerLayout';
import { DealCalculator } from '@modules/ContactManager/components/Tabs/shared/DealCalculator/DealCalculator';
import { gridDateTimeFormatter, queryParams, withQueryParams } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { OneToOneConsentPresentedAndAccepted, Roles } from '@shared/constants/constants';
import { useLeadSource } from '@shared/hooks/useLeadSource';
import { useQueryState } from '@shared/hooks/useQueryState';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { selectWorkflowNextStep } from '@store/reducers/contactManagerSlice';
import { GridColActionsWrapper } from '@styles/PhoenixGrid/GridStyles';
import { HorizontalScrollStyle } from '@styles/StyledDiv';

import { getAssetValue } from '../Assets/utils';
import { requestDealToggle } from '../Deals/queries';
import { IContactDetails } from '../Contact/components/OptOutModal/interfaces';
import { DealcalculatorErrorModal } from './components/DealCalculatorErrorModal/DealCalculatorErrorModal';
import { LeadModal } from './components/LeadModal';
import { LeadsHeader } from './components/LeadsTabComponents';
import { OverrideModal } from './components/OverrideModal/OverrideModal';
import { OverrideFormFormikStateType } from './components/OverrideModal/types';
import { LeadsCommunicationTable } from './LeadsCommunicationTable';
import { requestCreateLead, requestCreateNewDeal, requestUpdateLead } from './requests';
import { StyleOverride } from './StyleOverride';
import { ILead, ILeadSource } from './types/interfaces/interfaces';

interface ILeadsTableProps {
  selectLeadId: (leadId: string) => void;
  selectedLeadId: string;
  contactDetails: IContactDetails;
  reloadLeads: KeyedMutator<ILead[]>;
  reloadContactAlerts: () => void;
}

export const Leads = ({ selectLeadId, selectedLeadId, contactDetails, reloadLeads, reloadContactAlerts }: ILeadsTableProps) => {
  const [dealIdParam, setDealIdParam] = useQueryState('dealId');
  const [, setAssetIdParam] = useQueryState('assetId');

  const { isAdmin, userHasRoles } = useUserAccount();
  const navigate = useNavigate();
  const params = queryParams();
  const { leadSourceData: sources } = useLeadSource<ILeadSource[]>();
  const workflowDetailsNextStep = useSelector(selectWorkflowNextStep);

  const [secondarySources, setSecondarySources] = useState<ILeadSource[]>([]);
  const [lead, setLead] = useState<ILead | null>(null);
  const [isOpen, setOpen] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultPrimarySource, setDefaultPrimarySource] = useState<ILeadSource>({} as ILeadSource);
  const [overridePopupOpenStatus, handleOverridePopupOpenStatus] = useState(false);
  const [isOpenDealCalculatorErrorModal, setIsOpenDealCalculatorErrorModal] = useState(0);
  const [isOpenDealCalculatorModal, setIsOpenDealCalculatorModal] = useState(false);
  const [calculatorData, setCalculatorData] = useState({});

  const { data: leads = [], mutate: mutateLeads } = useSWR<ILead[]>(`/${SERVICES.LEAD}/contacts/${params.id}/leadswithoverride`, {
    revalidateOnMount: false,
  });
  const { data: headerData } = useSWR<IContactHeaderCreditScore>(`/${SERVICES.CONTACT}/contacts/${params?.id}/header`);

  const showOverride = isAdmin || userHasRoles([Roles.DEAL_CALC_OVERRIDE]);

  const sortedLeads = useMemo(() => {
    const sortedLeads = [...leads]?.sort((a, b) => b.createdDate.localeCompare(a.createdDate));

    return sortedLeads;
  }, [leads]);

  useEffect(() => {
    if (sortedLeads.length) {
      const defaultLead = sortedLeads.find((lead) => lead.id === selectedLeadId) || null;
      if (defaultLead) {
        handleLeadSelect(defaultLead);
        setLead(defaultLead);
      }
    }
  }, [selectedLeadId, sortedLeads]);

  useEffect(() => {
    mutateLeads();
  }, [workflowDetailsNextStep]);

  useEffect(() => {
    if (sources?.length && sources.length > 0) {
      const secSources = sources.filter((s) => s.isSecondary);
      setSecondarySources(secSources);
      const primary = sources.find((s) => s.name === 'PHONE_APPEND') as ILeadSource;
      setDefaultPrimarySource(primary);
    }
  }, [sources]);

  useEffect(() => {
    if (leads && params?.dealId && !selectedLeadId) {
      const defaultLead = leads?.find((lead) => lead?.pickedDealId === params?.dealId);

      if (defaultLead) {
        handleLeadSelect(defaultLead);
      }
    }
  }, [leads, params?.dealId, selectedLeadId]);

  const dealCalcHandler = (score: number) => {
    setCalculatorData({
      estimatedScore: score,
      isEstimatedScore: true,
    });
    setIsOpenDealCalculatorModal(true);
    setIsOpenDealCalculatorErrorModal(0);
  };

  const onCalculate = useCallback(() => {
    //Consider Tu default score type which is Tu from BE and TuV4 in UI
    const creditScore = headerData?.creditScore.find((v: ICreditScore) => v?.bureauType === 'Transunion' && v?.scoreType === 'Tu')
      ?.fico;
    if (!creditScore) {
      setIsOpenDealCalculatorErrorModal((prevState) => prevState + 1);
    } else {
      const finalScore = creditScore.includes('/') ? creditScore.split('/')[0] : creditScore;
      setCalculatorData({
        estimatedScore: Number(finalScore),
        isEstimatedScore: false,
      });
      setIsOpenDealCalculatorModal(true);
    }
  }, [headerData]);

  const hasOneToOneConsentYes = (params: any) => {
    // disabled for now
    return true;

    return (
      params.row.oneToOneConsentPresentedAndAccepted?.toString().toLocaleLowerCase() ===
      OneToOneConsentPresentedAndAccepted.Yes.toLocaleLowerCase()
    );
  };

  const isEditAssetDisabled = (params: GridRenderCellParams<ILead>) => {
    const hasAsset = !!params?.row?.assetId;
    const assetWithNonZeroAssetTag = params?.row?.assetTag?.trim() !== '0';

    const res = !hasOneToOneConsentYes(params) || (!isAdmin && (hasAsset && assetWithNonZeroAssetTag));
    return res;
  };

  const columns: GridColDef[] = useMemo(
    () => [
      dataGridCol('numericId', {
        headerName: 'ID',
        minWidth: 100,
        flex: 1,
      }),
      dataGridCol('assetTag', {
        headerName: 'Asset',
        minWidth: 200,
        flex: 4,
        valueGetter: (params) => getAssetValue(params?.value),
      }),
      dataGridCol('primarySourceName', {
        headerName: 'Pri Source',
        minWidth: 130,
        flex: 2,
      }),
      dataGridCol('secondarySourceName', {
        headerName: 'Sec Source',
        minWidth: 100,
        flex: 2,
      }),
      dataGridCol('createdDate', {
        headerName: 'Lead Created Date',
        type: 'dateTime',
        valueFormatter: gridDateTimeFormatter,
        minWidth: 105,
        flex: 2,
      }),
      dataGridCol('noOfNotes', {
        headerName: '# Comms',
        minWidth: 90,
        flex: 1,
      }),
      dataGridCol('lastNoteDateTime', {
        headerName: 'Last Comm',
        minWidth: 100,
        flex: 2,
        type: 'dateTime',
        valueFormatter: gridDateTimeFormatter,
      }),
      dataGridCol('currentWorkFlowStepName', {
        headerName: 'Workflow Step ',
        minWidth: 100,
        flex: 2,
      }),
      dataGridCol('currentWorkFlowStepDate', {
        headerName: 'Step Date',
        type: 'dateTime',
        valueFormatter: gridDateTimeFormatter,
        minWidth: 105,
        flex: 2,
      }),
      dataGridCol('assignedUser', {
        headerName: 'User',
        valueGetter: ({ value }) => value?.name,
        minWidth: 90,
        flex: 2,
      }),
      dataGridCol('lastActivity', {
        minWidth: 90,
        flex: 2,
      }),
      dataGridCol('lastActivityDate', {
        headerName: 'Activity Date',
        type: 'dateTime',
        valueFormatter: gridDateTimeFormatter,
        minWidth: 105,
        flex: 2,
      }),
      dataGridCol('actions', {
        minWidth: 120,
        renderCell: (params) => (
          <GridColActionsWrapper margin="-12px">
            <IconButton
              disabled={isEditAssetDisabled(params)}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <PencilIcon
                height={'20px'}
                className={hasOneToOneConsentYes(params) ? 'actions-icon' : ''}
              />
            </IconButton>

            <IconButton
              disabled={!params.row.showDealCalculatorButton || !hasOneToOneConsentYes(params)}
              onClick={(e) => {
                e.stopPropagation();
                onCalculate();
              }}
            >
              <CalculatorIcon
                className={params.row.showDealCalculatorButton && hasOneToOneConsentYes(params) ? 'actions-icon' : ''}
                height="20px"
                width="20px"
              />
            </IconButton>

            {showOverride && params.row.showOverrideDealButton && (
              <IconButton
                disabled={params.row.disableOverrideDealButton || !hasOneToOneConsentYes(params)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOverridePopupOpenStatus(true);
                }}
              >
                <PlusIcon
                  className={!params.row.disableOverrideDealButton && hasOneToOneConsentYes(params) ? 'actions-icon' : ''}
                  height="20px"
                  width="20px"
                />
              </IconButton>
            )}
          </GridColActionsWrapper>
        ),
      }),
    ],
    [showOverride, headerData]
  );

  const handleLeadSelect = useCallback((leadData: ILead) => {
    selectLeadId(leadData.id);
    setAssetIdParam(leadData.assetId);
    setDealIdParam(leadData?.pickedDealId || '');

    setLead(leadData);
  }, []);

  const handleSubmit = async (data: ILead) => {
    setIsLoading(true);
    let postData;
    if ((data.secondarySourceId === '' || data.secondarySourceId === null) && data.id) {
      postData = { ...data, contactId: params.id, secondarySourceId: null, secondarySourceName: '' };
    } else {
      postData = { ...data, contactId: params.id };
    }
    if (postData.id) {
      await requestUpdateLead(postData);
    } else {
      postData.oneToOneConsentPresentedAndAccepted = OneToOneConsentPresentedAndAccepted.Yes;
      await requestCreateLead(postData);
    }
    handleClose();
    mutateLeads();
    reloadLeads();
    setIsLoading(false);
  };

  const handleCreate = () => {
    setOpen((prevState) => prevState + 1);
    setLead({} as ILead);
  };

  const handleEdit = (data: ILead) => {
    setOpen((prevState) => prevState + 1);
    setLead(data);
  };

  const handleClose = () => {
    setOpen(0);
    setLead(null);
  };

  const handleSubmitOverride = async (data: OverrideFormFormikStateType) => {
    if (!lead?.assetId) {
      Notistack.generalError('No asset associated with this lead');
    } else {
      setIsLoading(true);
      const dataObj = await requestCreateNewDeal(lead, data, params.id);
      if (dataObj) {
        const { data: deal } = dataObj;
        setDealIdParam(deal.id);
        mutateLeads();
        handleOverridePopupOpenStatus(false);
        await requestDealToggle(deal.id);
        navigate(withQueryParams('/contact-manager', { tab: TabIndexEnum.Deals, dealId: deal.id }));
      }
      setIsLoading(false);
    }
  };

  return (
    <ContactManagerLayout>
      <StyleOverride />

      <LeadsHeader>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 2 }}
        >
          <Button
            variant="contained"
            onClick={handleCreate}
          >
            Add New Lead
          </Button>
        </Stack>
      </LeadsHeader>
      <HorizontalScrollStyle>
        <PhoenixGrid
          autoHeight
          noButton
          withHeader
          canNotCopy
          className="leads"
          selectionModel={lead?.id ? [lead.id] : []}
          columns={columns}
          rows={sortedLeads}
          loading={!leads}
          pageSize={5}
          onRowClick={(data) => handleLeadSelect(data?.row)}
        />
      </HorizontalScrollStyle>

      {lead?.id && (
        <LeadsCommunicationTable
          lead={lead}
          mutateLeads={mutateLeads}
          contactDetails={contactDetails}
        />
      )}

      <LeadModal
        modalIsOpen={isOpen}
        isLoading={isLoading}
        handleSubmit={handleSubmit}
        handleClose={handleClose}
        currentLead={lead}
        secondarySources={secondarySources}
        defaultPrimarySource={defaultPrimarySource}
      />

      {overridePopupOpenStatus && (
        <OverrideModal
          isLoading={isLoading}
          handleClose={() => handleOverridePopupOpenStatus(false)}
          handleSubmit={(data) => handleSubmitOverride(data)}
        />
      )}

      {!!isOpenDealCalculatorErrorModal && (
        <DealcalculatorErrorModal
          DealcalcErrModalPostiton={isOpenDealCalculatorErrorModal}
          dealCalcHandler={dealCalcHandler}
          handleClose={() => setIsOpenDealCalculatorErrorModal(0)}
        />
      )}

      {isOpenDealCalculatorModal && (
        <DealCalculator
          open
          refreshLeads={mutateLeads}
          handleClosePopUp={() => setIsOpenDealCalculatorModal(false)}
          assetNumericId={lead?.assetNumericId}
          numericId={lead?.numericId}
          reloadContactAlerts={reloadContactAlerts}
          data={{
            ...calculatorData,
            contactId: params.id,
            dealId: dealIdParam,
            leadId: lead?.id,
          }}
        />
      )}
    </ContactManagerLayout>
  );
};
